<template v-cloak>
  <Alert v-for="alert in alerts" :key="alert.id" :type="alert.type" :message="alert.title" :url="alert.url" />
</template>

<script>
import { defineComponent, ref, onMounted, onBeforeMount, computed } from 'vue';
import Alert from './components/Alert.vue';

export default defineComponent( {
  name: 'AlertBars',
  components: {
    Alert
  },
  props: {
    updateInterval: {
      type: Number,
      default: 60,
    },
    maxAlerts: {
      type: Number,
      default: 3,
    },
    exclude: {
      type: Array,
      default: () => [],
    },
    defaultRisks: {
      type: Array,
      default: () => [],
    },
    defaultIncidents: {
      type: Array,
      default: () => [],
    },
  },
  setup( props ) {
    const incidents =  ref( [...props.defaultIncidents] );
    const risks = ref( [...props.defaultRisks] );

    const loadingIncidents = ref( false );
    const loadingRisks = ref( false );

    const alerts = computed( () => {
      // Fill this.alerts based on the rules.
      const alerts = [],
          amountIncidents = !! incidents.value ? incidents.value.length : 0,
          amountRisks = !! risks.value ? risks.value.length : 0;
      let combineIncidents = false,
          combineRisks = false;

      // We need to combine things.
      if ( amountIncidents + amountRisks > props.maxAlerts ) {
        combineRisks = true;

        // We can show all risks if conditions met.
        combineIncidents = amountIncidents > 2;

        // We can show all risks if conditions met.
        combineRisks = ! ( amountRisks <= 2 && ( combineIncidents || amountIncidents <= 1 ) );
      }

      // Prepare alerts based on the combination logic.
      if ( combineIncidents ) {
        alerts.push( {
          type: 'alert',
          title: window.vrgz_variables.combined.incidents.replace( '{count}', amountIncidents ),
          url: window.vrgz_variables.page.incidents || '',
          combined: true
        } );
      } else if ( !! incidents.value ) {
        alerts.push( ...incidents.value.map( incident => ( { ...incident, type: 'alert' } ) ) );
      }

      if ( combineRisks ) {
        alerts.push( {
          type: 'warning',
          title: window.vrgz_variables.combined.risks.replace( '{count}', amountRisks ),
          url: window.vrgz_variables.page.risks || '',
          combined: true,
        } );
      } else if ( !! risks.value ) {
        alerts.push( ...risks.value.map( risk => ( { ...risk, type: 'warning' } ) ) );
      }

      return alerts;
    } );

    const fetchIncidents = async () => {
      if ( !! loadingIncidents.value ) return;

      try {
        loadingIncidents.value = true;
        const { url: uri, route } = window.vrgz_variables.rest;
        const url = new URL( `${ uri }${route}/incidents` );
        url.searchParams.append( 'active', '1' );
        if ( props.exclude.length > 0 ) {
          url.searchParams.append( 'exclude', props.exclude.join( ',' ) );
        }

        await fetch( url.toString() )
            .then( response => {
              if ( ! response.ok ) {
                throw new Error( 'Network response was not ok' );
              }
              return response.json();
            } )
            .then( data => incidents.value = data )
            .finally( () => loadingIncidents.value = false )
            .catch( error => console.error( 'Error fetching comments:', error ) );
      } catch ( error ) {
        console.error( 'Error fetching comments:', error );
      }
    };
    const fetchRisks = async () => {
      if ( !! loadingIncidents.value ) return;

      try {
        loadingRisks.value = true;

        const { url: uri, route } = window.vrgz_variables.rest;

        const url = new URL( `${ uri }${ route }/risks` );
        if ( props.exclude.length > 0 ) {
          url.searchParams.append( 'exclude', props.exclude.join( ',' ) );
        }

        await fetch( url.toString() )
            .then( response => {
              if ( ! response.ok ) {
                throw new Error( 'Network response was not ok' );
              }
              return response.json();
            } )
            .then( data => risks.value = data )
            .finally( () => loadingRisks.value = false )
            .catch( error => console.error( 'Error fetching comments:', error ) );
      } catch ( error ) {
        console.error( 'Error fetching comments:', error );
      }
    };

    /**
     * Fetch incidents and risks async.
     * @return {Promise<void>}
     */
    const fetchData = async () => {
      await fetchIncidents();
      await fetchRisks();
    };

    onBeforeMount( fetchData );
    onMounted( () => setInterval( fetchData, props.updateInterval * 1000 ) );

    return {
      alerts
    };
  },
} );
</script>
